import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import HomePage from './components/HomePage';
import LoveSpreadPage from './components/LoveSpreadPage';
import CareerSpreadPage from './components/CareerSpreadPage';
import PersonalGrowthSpreadPage from './components/PersonalGrowthSpreadPage';
import MentalHealingSpreadPage from './components/MentalHealingSpreadPage';
import GeneralSpreadPage from './components/GeneralSpreadPage';
import SpreadDrawingPage from './components/SpreadDrawingPage';
import ImportPage from './components/ImportPage';
import DiaryPage from './components/DiaryPage';
import ReadingDetail from './components/ReadingDetail';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import Header from './components/Header';
import Footer from './components/Footer';
import NotFound from './components/NotFound';
import ProtectedRoute from './components/auth/ProtectedRoute';
import WelcomeModal from './components/WelcomeModal';
import analytics, { initGA } from './services/analytics';
import logger from './utils/logger';
import usePageTracking from './hooks/usePageTracking';
import useSeoMeta from './hooks/useSeoMeta';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  const isSpreadDrawingPage = location.pathname === '/spread-drawing';
  const isAuthPage = location.pathname === '/login';
  
  // Track page views automatically
  usePageTracking();

  // Keep the tab title/description in sync on client-side navigation.
  // The entry document's full head is produced by functions/_middleware.js.
  useSeoMeta();

  // Show welcome modal for new users (you can add logic to check if user is new)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isNewUser = urlParams.get('welcome') === 'true';
    
    if (user && isNewUser) {
      setShowWelcomeModal(true);
    }
  }, [user, location.search]);

  return (
    <div className="App">
      {!isSpreadDrawingPage && !isAuthPage && <Header />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          <Route path="/love-spread" element={<LoveSpreadPage />} />
          <Route path="/career-spread" element={<CareerSpreadPage />} />
          <Route path="/personal-growth-spread" element={<PersonalGrowthSpreadPage />} />
          <Route path="/mental-healing-spread" element={<MentalHealingSpreadPage />} />
          <Route path="/general-spread" element={<GeneralSpreadPage />} />
          <Route path="/spread-drawing" element={<SpreadDrawingPage />} />
          <Route 
            path="/diary" 
            element={
              <ProtectedRoute>
                <DiaryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/import" 
            element={
              <ProtectedRoute>
                <ImportPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/reading/:id" 
            element={
              <ProtectedRoute>
                <ReadingDetail />
              </ProtectedRoute>
            }
          />
          {/* Must stay last. The edge middleware returns HTTP 404 for unmatched
              paths; this supplies the body so a 404 is not a blank page. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* Same visibility rule as <Header /> above: hidden on the immersive
          card-draw flow and the auth page. */}
      {!isSpreadDrawingPage && !isAuthPage && <Footer />}

      {/* Welcome Modal for new users */}
      {showWelcomeModal && (
        <WelcomeModal onClose={() => setShowWelcomeModal(false)} />
      )}
    </div>
  );
}

function App() {
  // Initialize Google Analytics on app start
  useEffect(() => {
    try {
      initGA();
    } catch (error) {
      logger.warn('Failed to initialize analytics:', error);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <AppContent />
            {/* React Query DevTools - only shows in development */}
            <ReactQueryDevtools initialIsOpen={false} />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
